// Step 1: File Selection
import { useState, useCallback, useEffect, useRef } from 'react';
import { Upload, X, Cloud } from 'lucide-react';
import { useUploadWizardContext } from '../context/UploadWizardContext';
import { formatBytes } from '@utils/format';
import { cn } from '@utils/classNames';
import { DropboxAuth } from '@features/upload/components/DropboxAuth';
import { DropboxBrowser } from '@features/upload/components/DropboxBrowser';
import { groupLocalFiles, syncGroupsWithFiles } from '../utils/groupLocalFiles';

export const Step1FileSelection: React.FC = () => {
  const { data, setData } = useUploadWizardContext();
  const [dragActive, setDragActive] = useState(false);
  const [duplicateFiles, setDuplicateFiles] = useState<File[]>([]);
  const [showDropboxAuth, setShowDropboxAuth] = useState(false);
  const [showDropboxBrowser, setShowDropboxBrowser] = useState(false);
  const [dropboxAccessToken, setDropboxAccessToken] = useState<string | null>(null);

  const dropboxClientId = import.meta.env.VITE_DROPBOX_CLIENT_ID || '';
  const cacheClearedRef = useRef(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Nach OAuth-Rückkehr zur Cloud: Callback setzt dropbox_resume_cloud_upload → Dropbox-Browser einmal öffnen
  useEffect(() => {
    if (!dropboxClientId) return;
    const token = sessionStorage.getItem('dropbox_access_token');
    const resume = sessionStorage.getItem('dropbox_resume_cloud_upload') === '1';
    if (token && resume && !dropboxAccessToken) {
      sessionStorage.removeItem('dropbox_resume_cloud_upload');
      setDropboxAccessToken(token);
      setShowDropboxAuth(false);
      setShowDropboxBrowser(true);
    }
  }, [dropboxClientId]); // dropboxAccessToken absichtlich nicht in deps, nur beim Mount prüfen

  const makeUniqueName = useCallback((originalName: string, takenNames: Set<string>) => {
    const dotIndex = originalName.lastIndexOf('.');
    const hasExt = dotIndex > 0 && dotIndex < originalName.length - 1;
    const base = hasExt ? originalName.slice(0, dotIndex) : originalName;
    const ext = hasExt ? originalName.slice(dotIndex) : '';

    let i = 2;
    let candidate = `${base} (${i})${ext}`;
    while (takenNames.has(candidate)) {
      i += 1;
      candidate = `${base} (${i})${ext}`;
    }
    return candidate;
  }, []);

  const addIncomingFiles = useCallback((incoming: File[]) => {
    const audioFiles = incoming.filter((file) => file.type.startsWith('audio/'));
    if (audioFiles.length === 0) return;

    const takenNames = new Set<string>();
    data.files.forEach((f) => takenNames.add(f.name));

    const toAdd: File[] = [];
    const duplicates: File[] = [];

    audioFiles.forEach((file) => {
      if (takenNames.has(file.name)) {
        duplicates.push(file);
      } else {
        toAdd.push(file);
        takenNames.add(file.name);
      }
    });

    if (toAdd.length > 0) {
      setData((prev) => {
        const newFiles = [...prev.files, ...toAdd];
        const groups =
          prev.groups.length > 0
            ? syncGroupsWithFiles(newFiles, prev.groups)
            : groupLocalFiles(newFiles);
        return { files: newFiles, groups };
      });
    }

    if (duplicates.length > 0) {
      setDuplicateFiles(duplicates);
    }
  }, [data.files, setData]);

  const uploadDuplicatesAsCopies = useCallback(() => {
    if (duplicateFiles.length === 0) return;

    const takenNames = new Set<string>();
    data.files.forEach((f) => takenNames.add(f.name));

    const renamed = duplicateFiles.map((file) => {
      const uniqueName = makeUniqueName(file.name, takenNames);
      takenNames.add(uniqueName);
      return new File([file], uniqueName, { type: file.type, lastModified: file.lastModified });
    });

    setData((prev) => {
      const newFiles = [...prev.files, ...renamed];
      return { files: newFiles, groups: groupLocalFiles(newFiles) };
    });
    setDuplicateFiles([]);
  }, [data.files, duplicateFiles, makeUniqueName, setData]);

  const cancelDuplicatePrompt = useCallback(() => {
    setDuplicateFiles([]);
  }, []);

  // CRITICAL: Clear metadata cache when files are first added (only once per upload session)
  useEffect(() => {
    if (data.files.length > 0 && !cacheClearedRef.current) {
      import('@services/metadata.service').then(({ metadataService }) => {
        metadataService.clearAllCache();
        console.log('🗑️ [Step1] Cleared metadata cache for new upload');
        cacheClearedRef.current = true;
      });
    } else if (data.files.length === 0) {
      // Reset flag when files are cleared
      cacheClearedRef.current = false;
      // CRITICAL: Reset file input when files are cleared
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
        console.log('🔄 [Step1] File input reset');
      }
    }
  }, [data.files.length]);

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const files = Array.from(e.dataTransfer.files);
    
    if (files.length > 0) {
      addIncomingFiles(files);
    }
  }, [addIncomingFiles]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      addIncomingFiles(files);
      
      // CRITICAL: Reset file input immediately after reading files
      // This prevents the same file from being selected again and ensures fresh state
      if (e.target) {
        e.target.value = '';
        console.log('🔄 [Step1] File input reset after selection');
      }
    }
  };

  const removeFile = (index: number) => {
    const fileToRemove = data.files[index];
    if (!fileToRemove) return;

    setData((prev) => {
      const newFiles = prev.files.filter((_, i) => i !== index);
      return {
        files: newFiles,
        groups: syncGroupsWithFiles(newFiles, prev.groups),
      };
    });
  };

  const handleDropboxAuthSuccess = (accessToken: string) => {
    setDropboxAccessToken(accessToken);
    setShowDropboxAuth(false);
    setShowDropboxBrowser(true);
  };

  const openDropboxImport = useCallback(() => {
    if (!dropboxClientId) return;
    const token = sessionStorage.getItem('dropbox_access_token');
    if (token) {
      setDropboxAccessToken(token);
      setShowDropboxAuth(false);
      setShowDropboxBrowser(true);
    } else {
      setShowDropboxAuth(true);
    }
  }, [dropboxClientId]);

  const handleDropboxFileSelect = (files: File[]) => {
    addIncomingFiles(files);
    setShowDropboxBrowser(false);
    setDropboxAccessToken(null);
  };

  const handleDropboxCancel = () => {
    setShowDropboxAuth(false);
    setShowDropboxBrowser(false);
    setDropboxAccessToken(null);
  };

  if (showDropboxAuth && dropboxClientId) {
    const currentPath = window.location.pathname;
    return (
      <div className="space-y-6">
        <DropboxAuth
          clientId={dropboxClientId}
          returnPath={currentPath}
          onAuthSuccess={handleDropboxAuthSuccess}
          onCancel={handleDropboxCancel}
        />
      </div>
    );
  }

  if (showDropboxBrowser && dropboxAccessToken) {
    return (
      <div className="space-y-6">
        <DropboxBrowser
          accessToken={dropboxAccessToken}
          onFileSelect={handleDropboxFileSelect}
          onCancel={handleDropboxCancel}
        />
      </div>
    );
  }

  return (
    <>
      <div className="space-y-3 sm:space-y-4 md:space-y-2.5 lg:space-y-3 xl:space-y-3.5 w-full">
        <div className="text-center mb-2 sm:mb-3 md:mb-2 lg:mb-2.5 xl:mb-3">
          <h2 className="font-dela text-lg sm:text-xl md:text-lg lg:text-xl xl:text-2xl text-white mb-1 sm:mb-1.5 md:mb-1 lg:mb-1.5">Select files</h2>
          <p className="text-white/60 font-inter text-sm sm:text-base md:text-xs lg:text-sm xl:text-base">Choose the audio files you want to upload</p>
        </div>

        <div
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          className={cn(
            'relative border-2 border-dashed rounded-lg sm:rounded-xl lg:rounded-2xl p-4 sm:p-6 md:p-4 lg:p-5 xl:p-6 text-center transition-all w-full',
            dragActive
              ? 'border-primary bg-primary/20 shadow-lg shadow-primary/30 scale-[1.02]'
              : 'border-white/30 hover:border-primary/50 bg-white/5 hover:bg-white/10'
          )}
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="audio/*"
            onChange={handleFileChange}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
          />
          
          <div className="flex flex-col items-center">
            <Upload className={cn(
              "w-12 h-12 sm:w-16 sm:h-16 md:w-10 md:h-10 lg:w-12 lg:h-12 xl:w-14 xl:h-14 mx-auto mb-2 sm:mb-3 md:mb-2 lg:mb-2.5 transition-all",
              dragActive ? "text-primary scale-110" : "text-primary"
            )} />
            <h3 className={cn(
              "mb-1 sm:mb-2 md:mb-1 lg:mb-1.5 font-codec font-bold text-base sm:text-lg md:text-base lg:text-lg xl:text-xl transition-colors",
              dragActive ? "text-primary" : "text-white"
            )}>
              {dragActive ? 'Drop files here' : 'Drag files here'}
            </h3>
            <p className="text-white/70 text-sm sm:text-base md:text-xs lg:text-sm xl:text-base mb-2 sm:mb-2.5 md:mb-2 lg:mb-2.5">or click to browse</p>
            <p className="text-xs sm:text-sm md:text-[11px] lg:text-xs xl:text-sm text-white/50 font-inter">
              Supported: MP3, WAV, FLAC, AIFF, OGG
            </p>
          </div>
        </div>

        {dropboxClientId && (
          <>
            <div className="flex items-center gap-2 sm:gap-3 md:gap-2.5 lg:gap-3 my-2 sm:my-3 md:my-2.5 lg:my-3">
              <div className="flex-1 border-t border-white/20"></div>
              <span className="text-white/60 text-xs sm:text-sm md:text-xs lg:text-sm xl:text-base font-inter uppercase tracking-wider">OR</span>
              <div className="flex-1 border-t border-white/20"></div>
            </div>

            <button
              type="button"
              onClick={openDropboxImport}
              className="w-full flex items-center justify-center gap-2 sm:gap-3 md:gap-2 lg:gap-2.5 px-3 sm:px-4 md:px-3 lg:px-3.5 xl:px-4 py-2.5 sm:py-3 md:py-2 lg:py-2.5 xl:py-3 bg-white/5 hover:bg-white/10 border-2 border-white/20 hover:border-primary/50 rounded-lg sm:rounded-xl lg:rounded-2xl transition-all text-white font-codec font-bold text-sm sm:text-base md:text-sm lg:text-base xl:text-lg group"
            >
              <Cloud className="w-5 h-5 sm:w-6 sm:h-6 md:w-4 md:h-4 lg:w-4.5 lg:h-4.5 xl:w-5 xl:h-5 text-primary group-hover:scale-110 transition-transform flex-shrink-0" />
              <span>Import from Dropbox</span>
            </button>
          </>
        )}

        {data.files.length > 0 && (
          <div className="space-y-2 sm:space-y-2.5 md:space-y-2 lg:space-y-2.5 mt-2 sm:mt-3 md:mt-2.5 lg:mt-3 xl:mt-3.5">
            <h4 className="font-codec font-bold text-white text-sm sm:text-base md:text-base lg:text-lg xl:text-xl">
              Selected files ({data.files.length})
            </h4>
            <div className="space-y-1.5 sm:space-y-2 md:space-y-2 max-h-32 sm:max-h-40 md:max-h-32 lg:max-h-36 xl:max-h-40 overflow-y-auto pr-1.5">
              {data.files.map((file, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-2 sm:p-2.5 md:p-2 lg:p-2.5 xl:p-2.5 rounded-lg lg:rounded-xl border bg-white/5 hover:bg-white/10 border-white/10 transition-all group"
                  >
                    <div className="flex-1 min-w-0 pr-1.5">
                      <p className="font-inter font-bold text-white truncate text-xs sm:text-sm md:text-sm lg:text-base xl:text-lg">
                        {file.name}
                      </p>
                      <p className="text-xs sm:text-sm md:text-[11px] lg:text-xs xl:text-sm text-white/60 mt-0.5">
                        {formatBytes(file.size)}
                      </p>
                    </div>
                    <button
                      onClick={() => removeFile(index)}
                      className="ml-1.5 sm:ml-2 md:ml-2 p-1.5 sm:p-2 md:p-1.5 lg:p-1.5 text-red-400 hover:bg-red-500/20 hover:text-red-300 rounded-lg lg:rounded-xl transition-all opacity-70 group-hover:opacity-100 flex-shrink-0 min-h-[44px] min-w-[44px] flex items-center justify-center"
                      aria-label="Remove file"
                    >
                      <X className="w-4 h-4 sm:w-4.5 sm:h-4.5 md:w-4 md:h-4 lg:w-4 lg:h-4" />
                    </button>
                  </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Duplicate files prompt (English texts as requested) */}
      {duplicateFiles.length > 0 && (
        <div className="fixed inset-0 z-[10030] bg-black/60 backdrop-blur-md flex items-center justify-center p-4">
          <div className="w-full max-w-lg max-h-[90dvh] overflow-y-auto bg-dark-secondary/95 border border-white/10 rounded-xl shadow-2xl p-5 pb-[calc(1.25rem+env(safe-area-inset-bottom))]">
            <h3 className="font-inter font-bold text-white text-lg mb-2">Duplicate file detected</h3>
            <p className="text-white/60 font-inter text-sm mb-3">
              A file with the same name already exists. You can upload again as a renamed copy, or cancel.
            </p>
            <div className="max-h-40 overflow-y-auto space-y-1 mb-4">
              {duplicateFiles.map((f, idx) => (
                <div key={`${f.name}-${idx}`} className="text-sm text-white/80 font-inter truncate">
                  • {f.name}
                </div>
              ))}
            </div>
            <p className="text-white/40 font-inter text-xs mb-4">
              “Upload again” will add a copy with a new name (e.g. <span className="font-mono">“name (2).wav”</span>).
            </p>
            <div className="flex flex-col-reverse sm:flex-row gap-2 justify-end">
              <button
                type="button"
                onClick={cancelDuplicatePrompt}
                className="px-4 py-2 rounded-lg bg-white/10 text-white hover:bg-white/20 transition-all font-inter text-sm min-h-[44px]"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={uploadDuplicatesAsCopies}
                className="px-4 py-2 rounded-lg bg-gradient-to-r from-[#30d870] to-[#00ff61] text-black hover:scale-105 active:scale-95 transition-all font-inter font-semibold text-sm min-h-[44px]"
              >
                Upload again
              </button>
            </div>
          </div>
        </div>
      )}

    </>
  );
};
